import React, { useEffect, useState } from 'react';
import { adminService } from '../services/adminService';
import { Flag, Trash2, RefreshCw, Search, Info } from 'lucide-react'; // Remove Eye, use Info
import toast from 'react-hot-toast';

const renderStars = (rating) => {
  const stars = [];
  const rounded = Math.round(Number(rating) || 0);
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span key={i} style={{ color: i <= rounded ? '#fbbf24' : '#e5e7eb', fontSize: '1.1em' }}>
        ★
      </span>
    );
  }
  return <span>{stars}</span>;
};

const Ratings = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState(null);
  const [profileModal, setProfileModal] = useState({ open: false, user: null });

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    setLoading(true);
    try {
      const data = await adminService.getRatings();
      setRatings(data);
      setError(null);
    } catch (err) {
      setError('Failed to load ratings');
      setRatings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = async (id) => {
    try {
      await adminService.flagRating(id);
      toast.success('Rating flagged!');
      fetchRatings();
    } catch {
      toast.error('Failed to flag rating');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rating?')) return;
    try {
      await adminService.deleteRating(id);
      toast.success('Rating deleted!');
      fetchRatings();
    } catch {
      toast.error('Failed to delete rating');
    }
  };

  const handleProfileOpen = (user) => {
    setProfileModal({ open: true, user });
  };

  const handleProfileClose = () => {
    setProfileModal({ open: false, user: null });
  };

  const filteredRatings = ratings.filter(r => {
    const matchesSearch =
      search === '' ||
      (r.review && r.review.toLowerCase().includes(search.toLowerCase())) ||
      (r.reviewer?.name && r.reviewer.name.toLowerCase().includes(search.toLowerCase())) ||
      (r.reviewee?.name && r.reviewee.name.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus =
      !statusFilter || (r.status === statusFilter);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ratings</h1>
          <p className="text-gray-600">
            {error ? 'Failed to load ratings' : 'View and manage all user ratings'}
          </p>
        </div>
        <button onClick={fetchRatings} className="btn-secondary flex items-center gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by reviewer, reviewee, or comment..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="flagged">Flagged</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ratings...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={fetchRatings} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Ratings Table */}
      {!loading && !error && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stars</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRatings.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                      No ratings found.
                    </td>
                  </tr>
                ) : (
                  filteredRatings.map(r => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {r.reviewer?.name?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{r.reviewer?.name || '-'}</div>
                            <div className="text-sm text-gray-500">{r.reviewer?.email || '-'}</div>
                          </div>
                          {r.reviewer && (
                            <button
                              className="ml-2 text-blue-600 hover:text-blue-900"
                              title="View Profile"
                              onClick={() => handleProfileOpen(r.reviewer)}
                            >
                              <Info className="h-4 w-4" /> {/* Use Info icon instead of Eye */}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {r.reviewee?.name?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{r.reviewee?.name || '-'}</div>
                            <div className="text-sm text-gray-500">{r.reviewee?.email || '-'}</div>
                          </div>
                          {r.reviewee && (
                            <button
                              className="ml-2 text-blue-600 hover:text-blue-900"
                              title="View Profile"
                              onClick={() => handleProfileOpen(r.reviewee)}
                            >
                              <Info className="h-4 w-4" /> {/* Use Info icon instead of Eye */}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {r.campaign?.title || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {renderStars(r.rating)}
                        <span className="ml-2 text-xs text-gray-500">{r.rating}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {r.review || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {r.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {r.status || 'active'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            className="text-orange-600 hover:text-orange-900"
                            title="Flag"
                            disabled={r.status === 'flagged'}
                            onClick={() => handleFlag(r.id)}
                          >
                            <Flag className="h-4 w-4" />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                            onClick={() => handleDelete(r.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button className="btn-secondary">
                Previous
              </button>
              <button className="btn-secondary">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredRatings.length}</span> of{' '}
                  <span className="font-medium">{filteredRatings.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button className="btn-secondary rounded-l-md">
                    Previous
                  </button>
                  <button className="btn-secondary rounded-r-md">
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {profileModal.open && profileModal.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
              onClick={handleProfileClose}
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4">{profileModal.user.name}</h2>
            <div className="space-y-2">
              <p><strong>Email:</strong> {profileModal.user.email}</p>
              <p><strong>Status:</strong> <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{profileModal.user.status || 'active'}</span></p>
              <p><strong>Role:</strong> <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">{profileModal.user.role}</span></p>
              <p><strong>ID:</strong> {profileModal.user.id}</p>
              {profileModal.user.profilePicture && (
                <img
                  src={profileModal.user.profilePicture}
                  alt="Profile"
                  className="rounded mb-4 max-h-32"
                />
              )}
              <p><strong>Coins:</strong> {profileModal.user.coins ?? 'N/A'}</p>
              <p><strong>Joined:</strong> {profileModal.user.created_at ? new Date(profileModal.user.created_at).toLocaleDateString() : 'N/A'}</p>
              <p><strong>Last Active:</strong> {profileModal.user.last_active ? new Date(profileModal.user.last_active).toLocaleDateString() : 'N/A'}</p>
              <p><strong>Mobile:</strong> {profileModal.user.mobile || 'N/A'}</p>
              <p><strong>Location:</strong> {profileModal.user.location || 'N/A'}</p>
              <p><strong>Bio:</strong> {profileModal.user.bio || 'N/A'}</p>
            </div>
            <button className="btn-primary mt-6 w-full" onClick={handleProfileClose}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ratings;